import Link from '@components/common/Link/Link'
import Cross from '@components/ui/Icons/Cross/Cross'
import Media from '@components/ui/Media/Media'

import { useCartRemoveLineItem } from '@features/cart/api/cart-remove-line-items'
import { useCartUpdateLineItem } from '@features/cart/api/cart-update-line-items'
import { formatPriceV2, getLineItemTotalPrice } from '@lib/format-price'
import { CartLineItemFragment, StockPolicy } from '@lib/graphql/graphql'
import { isAvailableForPurchase } from '@lib/is-available-for-purchase'
import { MediaWithCropsFragment } from '@lib/umbraco/generated/types.generated'
import { generateMediaUrlV2 } from '@lib/utils'
import { selectAlpha2Code } from '@redux/globalSlice'
import { mapEdgesToItems } from '@utils/map'
import clsx from 'clsx'
import { FC } from 'react'
import { useSelector } from 'react-redux'
import s from '../Navbar.module.scss'
export interface CartItemProps {
  line: CartLineItemFragment
  className?: string
}
const CartItem: FC<CartItemProps> = ({ line, className }) => {
  const { mutateAsync: updateLineItem } = useCartUpdateLineItem()
  const { mutateAsync: removeLineItem } = useCartRemoveLineItem()
  const alpha2Code = useSelector(selectAlpha2Code)

  const handleCartLineQuantityChange = async (
    lineId: string,
    quantity: number,
  ) => {
    updateLineItem({ lineItemId: lineId, quantity })
  }

  const handleCartLineRemove = async (item: CartLineItemFragment) => {
    removeLineItem(item)
  }

  const discountApplications = mapEdgesToItems(line.discountApplications)

  return (
    <li className={clsx(s.cartPreviewItem, className)}>
      <button
        className={s.cartPreviewItemTrash}
        type="button"
        onClick={() => handleCartLineRemove(line)}
      >
        <Cross width={16} height={16} />
      </button>
      <div className={s.cartPreviewItemContainer}>
        <div className={s.cartPreviewItemInformation}>
          <Link
            link={{
              url: `/products/${line.productSlug}`,
            }}
            query={{ itemNumber: line.sku }}
            className={s.cartPreviewItemImageLink}
          >
            <div className={s.cartPreviewItemImageContainer}>
              <Media
                mediaWithCrops={
                  {
                    url: generateMediaUrlV2(line.variant?.image?.src),
                    media: {
                      __typename: 'Image',
                      url: generateMediaUrlV2(line.variant?.image?.src),
                    },
                  } as MediaWithCropsFragment
                }
                mediaClassName={s.cartPreviewItemImage}
                disableShopTheLook
              />
            </div>
          </Link>

          <div className={s.cartPreviewItemContent}>
            <span className={s.cartPreviewItemProductName}>
              {line.productName}
            </span>
            {!line.variant && (
              <span
                className={s.cartPreviewItemProductName}
                style={{ color: 'var(--discount-red)' }}
              >
                This item is no longer available
              </span>
            )}
            <span className={s.cartPreviewItemProductAttribute}>
              {line.variant?.selectedAttributes?.find(Boolean)?.value || ''}
            </span>
            <span className={s.quantityWrapper}>
              <button
                type="button"
                className={s.decreaseButton}
                disabled={line.quantity === 1 || !line.variant}
                onClick={() =>
                  handleCartLineQuantityChange(line.id, line.quantity - 1)
                }
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 16 16"
                  width="12"
                  height="12"
                >
                  <path d="M2 7.75A.75.75 0 0 1 2.75 7h10a.75.75 0 0 1 0 1.5h-10A.75.75 0 0 1 2 7.75Z"></path>
                </svg>
              </button>
              <span className={s.quantity}>{line.quantity}</span>
              <button
                type="button"
                className={s.increaseButton}
                onClick={() => {
                  handleCartLineQuantityChange(line.id, line.quantity + 1)
                }}
                disabled={
                  !isAvailableForPurchase(line?.variant?.availability) ||
                  (line.variant.availability.stockPolicy !=
                    StockPolicy.NotTracked &&
                    line.quantity >=
                      line.variant.availability.availableQuantity)
                }
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 16 16"
                  width="12"
                  height="12"
                >
                  <path d="M7.75 2a.75.75 0 0 1 .75.75V7h4.25a.75.75 0 0 1 0 1.5H8.5v4.25a.75.75 0 0 1-1.5 0V8.5H2.75a.75.75 0 0 1 0-1.5H7V2.75A.75.75 0 0 1 7.75 2Z"></path>
                </svg>
              </button>
            </span>
          </div>
          <div className={s.cartPreviewItemPrice}>
            {(line.unitPrice.discountedPrice ||
              discountApplications.length > 0) && (
              <span className={s.discountedPrice}>
                {formatPriceV2(alpha2Code, getLineItemTotalPrice(line), 2)}
              </span>
            )}
            <span
              className={clsx(s.originalPrice, {
                [s.strikethrough]: discountApplications.length > 0,
              })}
            >
              {formatPriceV2(
                alpha2Code,
                {
                  ...line.unitPrice.value,
                  centAmount: line.unitPrice.value.centAmount * line.quantity,
                },
                2,
              )}
            </span>
          </div>
        </div>
      </div>
    </li>
  )
}

export default CartItem
